import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';
import API_BASE_URL from '../config/api';
import UpgradeModal from './UpgradeModal';
import newsIcon from '../assets/news_logo_template_bw.gif';
import customerCareIcon from '../assets/icon_customer_care.gif';

const PLAN_LABELS = {
  free: 'Free Plan',
  monthly: 'Monthly Plan',
  annual: 'Annual Plan',
};

const PLAN_COLORS = {
  free: '#8b949e',
  monthly: '#3fb950',
  annual: '#3fb950',
};

const TopHeader = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, plan, graceActive, refreshPlan } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'switch' | 'cancel' | 'portal' | null
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  const dropdownRef = useRef(null);

  const getPageTitle = () => {
    switch (location.pathname) {
      case '/': return 'Home';
      case '/balances': return 'Balances';
      case '/about': return 'About';
      case '/exchanges': return 'Exchanges';
      case '/symbols': return 'Symbols';
      case '/funding-fees': return 'Funding Rates';
      case '/taker-maker': return 'Taker Maker Fees';
      case '/opportunities': return 'Opportunities';
      case '/trades': return 'Trades';
      case '/settings': return 'Settings';
      case '/login': return 'Login';
      default: return 'Home';
    }
  };

  const handleLogout = () => {
    trackEvent('Authentication', 'Logout', user?.email || 'Unknown User');
    setIsDropdownOpen(false);
    logout();
    navigate('/login');
  };

  const handleSwitchPlan = async () => {
    if (!confirmSwitch) {
      setConfirmSwitch(true);
      return;
    }
    const newPlan = plan === 'monthly' ? 'annual' : 'monthly';
    setActionLoading('switch');
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscriptions/switch-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.sub, newPlan }),
      });
      const data = await res.json();
      if (data.success) {
        await refreshPlan(user.sub);
        setConfirmSwitch(false);
        setIsDropdownOpen(false);
      } else {
        console.error('Switch plan error:', data.error);
      }
    } catch (err) {
      console.error('Switch plan request failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirmCancel) {
      setConfirmCancel(true);
      return;
    }
    setActionLoading('cancel');
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.sub }),
      });
      const data = await res.json();
      if (data.success) {
        await refreshPlan(user.sub);
        setConfirmCancel(false);
        setIsDropdownOpen(false);
      } else {
        console.error('Cancel error:', data.error);
      }
    } catch (err) {
      console.error('Cancel request failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBillingPortal = async () => {
    setActionLoading('portal');
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscriptions/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.sub }),
      });
      const data = await res.json();
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        console.error('Billing portal error:', data.error);
      }
    } catch (err) {
      console.error('Billing portal request failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setConfirmCancel(false);
    setConfirmSwitch(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setConfirmCancel(false);
        setConfirmSwitch(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isDropdownOpen]);

  const isPaidPlan = plan === 'monthly' || plan === 'annual';
  return (
    <div className="top-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          ☰
        </button>
        <div className="breadcrumb">
          <span style={{ fontSize: '20px', fontWeight: '600', color: '#ffffff' }}>
            {getPageTitle()}
          </span>
        </div>
      </div>

      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}

      <div className="user-controls">
        <a
          href="https://discordapp.com/channels/1484183121741090818/1484183122517032982"
          target="_blank"
          rel="noopener noreferrer"
          className="discord-link"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
        >
          <img src={newsIcon} alt="News" style={{ width: '40px', height: '40px', flexShrink: 0, objectFit: 'contain' }} />
          <span className="discord-link-label">News channel</span>
        </a>
        <a
          href="https://discordapp.com/channels/1484183121741090818/1484186803446480926"
          target="_blank"
          rel="noopener noreferrer"
          className="discord-link"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
        >
          <img src={customerCareIcon} alt="Customer support" style={{ width: '40px', height: '40px', flexShrink: 0, objectFit: 'contain' }} />
          <span className="discord-link-label">Customer support</span>
        </a>
        {isAuthenticated && graceActive && (
          <button
            onClick={() => setShowUpgradeModal(true)}
            style={{
              padding: '6px 14px', fontSize: '12px',
              backgroundColor: '#f0a000', border: 'none',
              borderRadius: '4px', color: '#0a0b0f',
              cursor: 'pointer', fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            Order subscription
          </button>
        )}
        {isAuthenticated ? (
          <div className="user-menu-container" ref={dropdownRef}>
            <button className="user-avatar-button" onClick={toggleDropdown} aria-label="User menu">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="16" fill="#2a2d3a" />
                <circle cx="16" cy="13" r="5" fill="#8b949e" />
                <path d="M6 26c0-5.523 4.477-9 10-9s10 3.477 10 9" fill="#8b949e" />
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="user-dropdown">
                {/* User info */}
                <div className="user-dropdown-header">
                  <span className="user-dropdown-name">{user?.name || 'User'}</span>
                  <span className="user-dropdown-email">{user?.email || ''}</span>
                </div>

                <div className="user-dropdown-divider" />

                {/* Current plan */}
                <div className="user-dropdown-item" style={{ cursor: 'default', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8b949e' }}>Actual Plan:</span>
                  <span style={{ color: PLAN_COLORS[plan] || '#8b949e', fontWeight: 600 }}>
                    {PLAN_LABELS[plan] || plan}
                  </span>
                </div>

                {/* Switch plan — only for paid users */}
                {isPaidPlan && (
                  <div
                    className="user-dropdown-item"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'default' }}
                  >
                    <span style={{ color: '#8b949e' }}>
                      {confirmSwitch ? 'Confirm switch:' : 'Switch to:'}
                    </span>
                    <button
                      onClick={handleSwitchPlan}
                      disabled={!!actionLoading}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        color: confirmSwitch ? '#f0a000' : '#ffffff',
                        fontWeight: 600, fontSize: 'inherit',
                        opacity: actionLoading === 'switch' ? 0.6 : 1,
                      }}
                    >
                      {actionLoading === 'switch'
                        ? 'Switching…'
                        : confirmSwitch
                          ? 'You will be charged now'
                          : plan === 'monthly' ? 'Annual ($150/yr)' : 'Monthly ($15/mo)'}
                    </button>
                  </div>
                )}

                {/* Cancel subscription — only for paid users */}
                {isPaidPlan && (
                  confirmCancel ? (
                    <button
                      className="user-dropdown-item"
                      onClick={handleCancelSubscription}
                      disabled={!!actionLoading}
                      style={{ color: '#f85149', textAlign: 'left', opacity: actionLoading === 'cancel' ? 0.6 : 1 }}
                    >
                      {actionLoading === 'cancel' ? 'Cancelling…' : 'Confirm cancel — access kept until period end'}
                    </button>
                  ) : (
                    <button
                      className="user-dropdown-item"
                      onClick={handleCancelSubscription}
                      style={{ color: '#8b949e', textAlign: 'left' }}
                    >
                      Cancel subscription
                    </button>
                  )
                )}

                {/* Billing portal — only for paid users */}
                {isPaidPlan && (
                  <button
                    className="user-dropdown-item"
                    onClick={handleBillingPortal}
                    disabled={!!actionLoading}
                    style={{ color: '#8b949e', textAlign: 'left', opacity: actionLoading === 'portal' ? 0.6 : 1 }}
                  >
                    {actionLoading === 'portal' ? 'Opening portal…' : 'Billing & Invoices'}
                  </button>
                )}

                <div className="user-dropdown-divider" />

                <button className="user-dropdown-item" onClick={() => { navigate('/terms'); setIsDropdownOpen(false); }} style={{ color: '#8b949e', textAlign: 'left' }}>
                  Terms &amp; Conditions
                </button>
                <button className="user-dropdown-item" onClick={() => { navigate('/privacy'); setIsDropdownOpen(false); }} style={{ color: '#8b949e', textAlign: 'left' }}>
                  Privacy Policy
                </button>

                <div className="user-dropdown-divider" />

                <a
                  href="https://discordapp.com/channels/1484183121741090818/1484183122517032982"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="user-dropdown-item"
                  style={{ color: '#8b949e', textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: '5px', textDecoration: 'none' }}
                >
                  <img src={newsIcon} alt="News" style={{ width: '14px', height: '14px', flexShrink: 0, objectFit: 'contain' }} />
                  News channel
                </a>
                <a
                  href="https://discordapp.com/channels/1484183121741090818/1484186803446480926"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="user-dropdown-item"
                  style={{ color: '#8b949e', textAlign: 'left', display: 'block', textDecoration: 'none' }}
                >
                  Customer support
                </a>

                <div className="user-dropdown-divider" />

                <button className="user-dropdown-item logout" onClick={handleLogout} style={{ color: '#8b949e' }}>
                  Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '6px 12px', fontSize: '12px',
              backgroundColor: '#238636', border: 'none',
              borderRadius: '4px', color: '#ffffff',
              cursor: 'pointer', fontWeight: '500'
            }}
          >
            Login
          </button>
        )}
      </div>
    </div>
  );
};

export default TopHeader;
